import { migrationOnlySquashOptional } from '@fullstory/util/optional-type-migration';
import {
  Funnel,
  GeneratedSearchie,
  Histogram as HistogramProto,
  IndividualList,
  PageList,
  SearchieDailyActivesData,
  SearchieMatchingUsersData,
  SearchieRageGradeData,
  SearchieSegmentHealthData,
  SearchieType,
  TimeRangeHistogramData as TimeRangeHistogramDataProto,
  TimeRangeHistogramResult as TimeRangeHistogramResultProto,
} from '@fullstory/proto/fs/services/alexa';

// similarData returns true if the old data and new data are similar
// enough, such that the new data can replace the old data in the rendered
// visualization without requiring explicit action from the user.
export function similarData(
  searchie: SearchieType,
  oldData: GeneratedSearchie | null | undefined,
  newData: GeneratedSearchie | null | undefined,
): boolean {
  switch (searchie) {
    case SearchieType.SEARCHIE_TYPE_MATCHING_USERS:
      return matchingUsers(oldData?.matchingUsersData, newData?.matchingUsersData);

    case SearchieType.SEARCHIE_TYPE_TIME_RANGE_COUNTS:
      return false;

    case SearchieType.SEARCHIE_TYPE_DAILY_ACTIVES:
      return dailyActives(oldData?.dailyActivesData, newData?.dailyActivesData);

    case SearchieType.SEARCHIE_TYPE_SEGMENT_HEALTH:
      return segmentHealth(oldData?.segmentHealthData, newData?.segmentHealthData);

    case SearchieType.SEARCHIE_TYPE_RAGE_GRADE:
      return frustratedSessions(oldData?.rageGradeData, newData?.rageGradeData);

    case SearchieType.SEARCHIE_TYPE_TOP_ERROR_CLICKS:
      return similarHistograms(
        oldData?.topErrorClicksData?.histogram,
        newData?.topErrorClicksData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_TOP_RAGE_CLICKS:
      return similarHistograms(
        oldData?.topRageClicksData?.histogram,
        newData?.topRageClicksData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_TOP_DEAD_CLICKS:
      return similarHistograms(
        oldData?.topDeadClicksData?.histogram,
        newData?.topRageClicksData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_MOBILE_DEVICES:
      return similarHistograms(
        oldData?.mobileDevicesData?.histogram,
        newData?.topRageClicksData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_REFERER_HOST:
      return similarHistograms(
        oldData?.trafficByRefererHostData?.histogram,
        newData?.trafficByRefererHostData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_UTM_SOURCE:
      return similarHistograms(
        oldData?.trafficByUtmSourceData?.histogram,
        newData?.trafficByUtmSourceData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_UTM_CAMPAIGN:
      return similarHistograms(
        oldData?.trafficByUtmCampaignData?.histogram,
        newData?.trafficByUtmCampaignData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_UTM_MEDIUM:
      return similarHistograms(
        oldData?.trafficByUtmMediumData?.histogram,
        newData?.trafficByUtmMediumData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_BROWSER:
      return similarHistograms(
        oldData?.trafficByBrowserData?.histogram,
        newData?.trafficByBrowserData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_DEVICE_BREAKDOWN:
      return similarHistograms(
        oldData?.deviceBreakdownData?.histogram,
        newData?.deviceBreakdownData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_SCREEN_RESOLUTIONS:
      return similarHistograms(
        oldData?.screenResolutionsData?.histogram,
        newData?.screenResolutionsData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_MOBILE_APP_VERSIONS:
      return similarHistograms(
        oldData?.mobileAppVersionsData?.histogram,
        newData?.mobileAppVersionsData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_MOBILE_RESOLUTIONS:
      return similarHistograms(
        oldData?.mobileResolutionsData?.histogram,
        newData?.mobileResolutionsData?.histogram,
      );
    case SearchieType.SEARCHIE_TYPE_MOBILE_OS_VERSIONS:
      return similarHistograms(
        oldData?.mobileOsVersionsData?.histogram,
        newData?.mobileOsVersionsData?.histogram,
      );

    case SearchieType.SEARCHIE_TYPE_EVENT_FUNNEL:
      return eventFunnel(oldData?.eventFunnelData?.funnel, newData?.eventFunnelData?.funnel);

    case SearchieType.SEARCHIE_TYPE_MOST_ACTIVE_USERS:
      return mostActiveUsers(
        oldData?.mostActiveUsersData?.individualList,
        newData?.mostActiveUsersData?.individualList,
      );

    case SearchieType.SEARCHIE_TYPE_TOP_PAGES:
      return pageList(oldData?.topPagesData?.pageList, newData?.topPagesData?.pageList);
    case SearchieType.SEARCHIE_TYPE_SLOW_PAGES:
      return pageList(oldData?.slowPagesData?.pageList, newData?.slowPagesData?.pageList);
    case SearchieType.SEARCHIE_TYPE_MOST_COMMON_ENTRY_PAGES:
      return pageList(
        oldData?.mostCommonEntryPagesData?.pageList,
        newData?.mostCommonEntryPagesData?.pageList,
      );

    case SearchieType.SEARCHIE_TYPE_FEEDBACK:
      return true;

    default:
      // if flag is unknown or not listed, return false meaning "not similar"
      return false;
  }
}

function matchingUsers(
  x: SearchieMatchingUsersData | null | undefined,
  y: SearchieMatchingUsersData | null | undefined,
): boolean {
  // If both are null return true, if only one is null return false
  if (!x || !y) {
    return !x && !y;
  }
  return equalArrays(x.users, y.users, (a, b) => a.indvId === b.indvId);
}

function mostActiveUsers(
  x: IndividualList | null | undefined,
  y: IndividualList | null | undefined,
): boolean {
  if (!x || !y) {
    return !x && !y;
  }
  if (x.countType !== y.countType) {
    return false;
  }
  return equalArrays(
    migrationOnlySquashOptional(x.data),
    migrationOnlySquashOptional(y.data),
    (a, b) => {
      return (
        migrationOnlySquashOptional(a?.individual).id ===
        migrationOnlySquashOptional(b?.individual).id
      );
    },
  );
}

function eventFunnel(x: Funnel | null | undefined, y: Funnel | null | undefined): boolean {
  return true; // always similar
}

function pageList(x: PageList | null | undefined, y: PageList | null | undefined): boolean {
  if (!x || !y) {
    return !x && !y;
  }
  return equalArrays(
    migrationOnlySquashOptional(x.data),
    migrationOnlySquashOptional(y.data),
    (a, b) => a.multiDatum?.label === b.multiDatum?.label,
  );
}

function dailyActives(
  x: SearchieDailyActivesData | null | undefined,
  y: SearchieDailyActivesData | null | undefined,
): boolean {
  if (!x || !y) {
    return !x && !y;
  }
  // current graphs must have the same N-day actives present
  if (
    !equalArrays(
      Object.keys(migrationOnlySquashOptional(x?.currentGraphs)).sort(),
      Object.keys(migrationOnlySquashOptional(y?.currentGraphs)).sort(),
    )
  ) {
    return false;
  }

  // past graphs must have same the same N-day actives present
  const xPast = x?.pastGraphs ?? {};
  const yPast = y?.pastGraphs ?? {};
  if (!equalArrays(Object.keys(xPast).sort(), Object.keys(yPast).sort())) {
    return false;
  }

  // each of the current graphs must be similar
  for (const k in x?.currentGraphs) {
    if (
      !similarGraphs(
        migrationOnlySquashOptional(migrationOnlySquashOptional(x?.currentGraphs)[k]?.data),
        migrationOnlySquashOptional(migrationOnlySquashOptional(y?.currentGraphs)[k]?.data),
      )
    ) {
      return false;
    }
  }
  return true;
}

function segmentHealth(
  x: SearchieSegmentHealthData | null | undefined,
  y: SearchieSegmentHealthData | null | undefined,
): boolean {
  if (!x || !y) {
    return !x && !y;
  }
  const totalGraphs = 3;
  if (
    migrationOnlySquashOptional(x.currentGraphs).length !== totalGraphs ||
    migrationOnlySquashOptional(y.currentGraphs).length !== totalGraphs
  ) {
    return false;
  }

  for (let i = 0; i < totalGraphs; i++) {
    if (
      !similarGraphs(
        migrationOnlySquashOptional(migrationOnlySquashOptional(x.currentGraphs)[i]?.data),
        migrationOnlySquashOptional(migrationOnlySquashOptional(y.currentGraphs)[i]?.data),
      )
    ) {
      return false;
    }
  }
  return true;
}

function frustratedSessions(
  x: SearchieRageGradeData | null | undefined,
  y: SearchieRageGradeData | null | undefined,
): boolean {
  if (!x || !y) {
    return !x && !y;
  }
  return equalArrays(
    migrationOnlySquashOptional(x.frustratedUsers),
    migrationOnlySquashOptional(y.frustratedUsers),
    (a, b) => a.indvId === b.indvId,
  );
}

function similarHistograms(
  x: HistogramProto | null | undefined,
  y: HistogramProto | null | undefined,
): boolean {
  if (!x || !y) {
    return !x && !y;
  }
  // NOTE: TotalCount does not matter for similarity
  if (x.countType !== y.countType) {
    return false;
  }
  return equalArrays(
    migrationOnlySquashOptional(x.data),
    migrationOnlySquashOptional(y.data),
    (a, b) => a.label === b.label,
  );
}

function similarGraphs(
  x: TimeRangeHistogramDataProto | null,
  y: TimeRangeHistogramDataProto | null,
): boolean {
  if (x === null || y === null) {
    throw new Error('Unexpected null in daily actives graph comparison');
  }
  if (x.granularity !== y.granularity) {
    return false;
  }
  return similarTimeRangeCounts(
    migrationOnlySquashOptional(x.counts),
    migrationOnlySquashOptional(y.counts),
  );
}

function similarTimeRangeCounts(
  x: TimeRangeHistogramResultProto[],
  y: TimeRangeHistogramResultProto[],
): boolean {
  if (x.length !== y.length) {
    return false;
  }

  // In order to be considered similar, the timestamps and values of all
  // entries but the last entry must be equal.
  const xx = x.slice(0, x.length - 1);
  const yy = y.slice(0, y.length - 1);

  return xx.every((_, i) => {
    return (
      xx[i]?.startAt === yy[i]?.startAt &&
      xx[i]?.endAt === yy[i]?.endAt &&
      xx[i]?.value === yy[i]?.value
    );
  });
}

// @VisibleForTesting
//
// equalArrays returns true if the given arrays x and y are considered equal,
// based on the given equality function eq. It also handles cases in which
// either or both of the given arrays could be null.
export function equalArrays<E>(
  x: E[] | null,
  y: E[] | null,
  eq: (a: E, b: E) => boolean = (a, b) => a === b,
): boolean {
  if (x == null && y == null) {
    // both null, so they're similar
    return true;
  }
  if (x == null || y == null) {
    // at this point exactly one is null, so they're not similar
    return false;
  }
  if (x.length !== y.length) {
    return false;
  }
  return x.every((_, i) => eq(x[i]!, y[i]!));
}
